import React, { useState, useEffect } from 'react';
import { X, Send, Paperclip, Mail, Loader2, ChevronDown, FolderOpen } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';

function arrayBufferToBase64(buffer) {
  if (!buffer) return null;
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = '';
  const len = bytes.byteLength;
  const chunkSize = 8192;
  for (let i = 0; i < len; i += chunkSize) {
    const chunk = bytes.subarray(i, Math.min(i + chunkSize, len));
    binary += String.fromCharCode.apply(null, chunk);
  }
  return btoa(binary);
}

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}`, 'Content-Type': 'application/json' });

/**
 * SendEmailModal — reusable across all document forms
 *
 * Props:
 *   isOpen: bool
 *   onClose: fn
 *   templateKey: string — e.g. 'air_waybill', 'can_issued', 'docket_created'
 *   variables: object — { customer_name, docket_no, ... }
 *   defaultToEmail: string
 *   pdfFetcher: async fn() => Uint8Array | null  — optional, fetches PDF bytes for attachment
 *   pdfFilename: string — e.g. 'AWB_SILLP000001.pdf'
 *   documentTitle: string — shown in modal header
 */
const SendEmailModal = ({
  isOpen,
  onClose,
  templateKey,
  variables = {},
  defaultToEmail = '',
  defaultSubject = '',
  defaultBody = '',
  pdfFetcher = null,
  pdfFilename = 'document.pdf',
  documentTitle = 'Document',
  docketId = null,        // enables "Save to Documents" feature
}) => {
  const { toast } = useToast();

  const [toEmail, setToEmail] = useState(defaultToEmail);
  const [ccEmail, setCcEmail] = useState('');
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);
  const [attachPdf, setAttachPdf] = useState(true);
  const [saveToDocs, setSaveToDocs] = useState(false);
  const [loading, setLoading] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showBodyPreview, setShowBodyPreview] = useState(true);

  // Load templates on open
  useEffect(() => {
    if (!isOpen) return;
    setToEmail(defaultToEmail);
    setSubject(defaultSubject);
    if (defaultBody) setBody(defaultBody);
    setAttachPdf(!!pdfFetcher);
    setSaveToDocs(false);
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, defaultToEmail, defaultSubject, defaultBody]);

  const fetchTemplates = async () => {
    setTemplateLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/email-templates`, { headers: getAuthHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      const list = Array.isArray(data) ? data : data.templates || [];
      setTemplates(list);
      // Auto-select matching template — only override subject/body if a match found
      const match = list.find(t => t.template_key === templateKey);
      if (match) applyTemplate(match, variables);
      // No fallback to first template — keep defaultSubject if no key match
    } catch (_) {}
    finally { setTemplateLoading(false); }
  };

  const renderTemplate = (str, vars) => {
    if (!str) return '';
    let result = str;
    for (const [k, v] of Object.entries(vars)) {
      result = result.replaceAll(`{{${k}}}`, v || '');
    }
    return result;
  };

  const applyTemplate = (tmpl, vars) => {
    setSelectedTemplate(tmpl);
    setSubject(renderTemplate(tmpl.subject, vars));
    setBody(renderTemplate(tmpl.html_body, vars));
  };

  const handleSend = async () => {
    if (!toEmail.trim()) { toast({ title: 'To email is required', variant: 'destructive' }); return; }
    if (!subject.trim()) { toast({ title: 'Subject is required', variant: 'destructive' }); return; }

    setLoading(true);
    try {
      let attachment_base64 = null;
      let pdfBytes = null;

      if ((attachPdf || saveToDocs) && pdfFetcher) {
        try {
          const raw = await pdfFetcher();
          if (raw) {
            pdfBytes = raw instanceof ArrayBuffer ? new Uint8Array(raw) : raw;
            attachment_base64 = arrayBufferToBase64(raw);
          }
        } catch (_) {
          toast({ title: 'Warning', description: 'Could not generate PDF — sending without attachment' });
        }
      }

      // ── Send Email ────────────────────────────────────────────────
      const ccList = ccEmail.split(',').map(e => e.trim()).filter(Boolean);
      const res = await fetch(`${backendUrl}/api/emails/send`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          to_email: toEmail.trim(),
          cc_emails: ccList,
          subject: subject.trim(),
          html_body: body,
          docket_id: docketId,
          attachment_base64: attachPdf ? attachment_base64 : null,
          attachment_filename: pdfFilename,
          attachment_mime_type: 'application/pdf',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to send email');

      // ── Save PDF to StoreDocuments (Google Drive) if requested ────
      if (saveToDocs && pdfBytes && docketId) {
        try {
          const blob = new Blob([pdfBytes], { type: 'application/pdf' });
          const formData = new FormData();
          formData.append('file', blob, pdfFilename);
          const params = new URLSearchParams({ document_title: documentTitle });
          await fetch(`${backendUrl}/api/dockets/${docketId}/stored-documents?${params}`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
            body: formData,
          });
          toast({ title: 'Email sent & PDF saved!', description: `${pdfFilename} saved to Documents` });
        } catch (e) {
          // Email already sent — show partial success
          toast({ title: 'Email sent!', description: `PDF save to Documents failed: ${e.message}`, variant: 'destructive' });
        }
      } else {
        toast({ title: 'Email sent!', description: `Sent to ${toEmail}` });
      }

      onClose();
    } catch (e) {
      toast({ title: 'Send failed', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
              <Mail className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-800 text-sm font-manrope">Send Email</p>
              <p className="text-xs text-gray-400 font-ibm">{documentTitle}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 text-gray-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Template selector */}
        {templates.length > 0 && (
          <div className="px-6 pt-4">
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-gray-500 font-ibm whitespace-nowrap">Template:</label>
              <div className="relative flex-1">
                <select
                  className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 appearance-none bg-white font-ibm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  value={selectedTemplate?.id || ''}
                  onChange={e => {
                    const t = templates.find(x => x.id === e.target.value);
                    if (t) applyTemplate(t, variables);
                  }}
                  data-testid="template-select"
                >
                  <option value="">-- Select Template --</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <ChevronDown className="w-3 h-3 absolute right-2 top-2.5 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {/* To */}
          <div>
            <label className="text-xs font-semibold text-gray-500 font-ibm mb-1 block">To *</label>
            <Input
              value={toEmail}
              onChange={e => setToEmail(e.target.value)}
              placeholder="recipient@example.com"
              className="text-sm"
              data-testid="email-to"
            />
          </div>

          {/* CC */}
          <div>
            <label className="text-xs font-semibold text-gray-500 font-ibm mb-1 block">CC <span className="font-normal">(comma separated)</span></label>
            <Input
              value={ccEmail}
              onChange={e => setCcEmail(e.target.value)}
              placeholder="cc1@example.com, cc2@example.com"
              className="text-sm"
              data-testid="email-cc"
            />
          </div>

          {/* Subject */}
          <div>
            <label className="text-xs font-semibold text-gray-500 font-ibm mb-1 block">Subject *</label>
            <Input
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Email subject"
              className="text-sm"
              data-testid="email-subject"
            />
          </div>

          {/* Body */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold text-gray-500 font-ibm">Email Body</label>
              <button
                onClick={() => setShowBodyPreview(p => !p)}
                className="text-xs text-blue-600 hover:underline font-ibm"
              >
                {showBodyPreview ? 'Edit HTML' : 'Preview'}
              </button>
            </div>
            {showBodyPreview ? (
              <div
                className="border border-gray-200 rounded-lg p-3 bg-white text-xs min-h-[140px] max-h-[260px] overflow-y-auto"
                dangerouslySetInnerHTML={{ __html: body }}
              />
            ) : (
              <Textarea
                value={body}
                onChange={e => setBody(e.target.value)}
                rows={7}
                className="text-xs font-mono"
                placeholder="<p>Email body HTML...</p>"
                data-testid="email-body"
              />
            )}
          </div>

          {/* Attach PDF + Save to Documents toggles */}
          {pdfFetcher && (
            <div className="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg border border-gray-100">
              {/* Attach PDF */}
              <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="attach-pdf-toggle">
                <div
                  onClick={() => setAttachPdf(p => !p)}
                  className={`relative w-10 h-5 rounded-full transition-colors ${attachPdf ? 'bg-blue-600' : 'bg-gray-200'}`}
                >
                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${attachPdf ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </div>
                <span className="text-xs font-ibm text-gray-700 flex items-center gap-1">
                  <Paperclip className="w-3 h-3" />
                  Attach PDF to email
                  <span className="text-gray-400">({pdfFilename})</span>
                </span>
              </label>

              {/* Save to Documents */}
              {docketId && (
                <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="save-to-docs-toggle">
                  <div
                    onClick={() => setSaveToDocs(p => !p)}
                    className={`relative w-10 h-5 rounded-full transition-colors ${saveToDocs ? 'bg-emerald-500' : 'bg-gray-200'}`}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${saveToDocs ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </div>
                  <span className="text-xs font-ibm text-gray-700 flex items-center gap-1">
                    <FolderOpen className="w-3 h-3" />
                    Also save PDF to Documents
                    <span className="text-gray-400">(Google Drive)</span>
                  </span>
                </label>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <p className="text-xs text-gray-400 font-ibm">From: info@stavan.co.in</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={loading}>Cancel</Button>
            <Button
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={handleSend}
              disabled={loading}
              data-testid="send-email-btn"
            >
              {loading
                ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Sending...</>
                : <><Send className="w-3.5 h-3.5 mr-1.5" />Send Email</>
              }
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendEmailModal;
